import React, { useState, useRef, useEffect } from "react";
import { MultipleSelectorProps } from "./interface";

const MultipleSelector: React.FC<MultipleSelectorProps> = ({
  options,
  selected,
  onApply,
  label,
  title,
  icon,
  t,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // When opening, initialize pending selection from applied selection (or all if empty)
  useEffect(() => {
    if (isOpen) {
      const initial =
        selected.length > 0 ? selected : options;
      setPendingSelection(initial);
    }
  }, [isOpen, options, selected]);

  const handleToggle = (item: string) => {
    setPendingSelection((prev) =>
      prev.includes(item) ? prev.filter((s) => s !== item) : [...prev, item]
    );
  };

  const handleApply = () => {
    const toApply = pendingSelection.length > 0 ? pendingSelection : options;
    onApply(toApply);
    setIsOpen(false);
  };

  const displayCount = selected.length || options.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isOpen
            ? "bg-blue-600 text-white border-blue-600 shadow-lg"
            : "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-blue-50 hover:border-blue-500 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:border-blue-400 dark:hover:text-blue-400 shadow-sm hover:shadow-md"
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="w-4 h-4 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>
        <span>{t(label)}</span>
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            isOpen ? "bg-white/20 text-white" : "bg-blue-600 text-white"
          }`}
        >
          {displayCount}
        </span>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl shadow-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 backdrop-blur-sm animate-in fade-in-0 zoom-in-95 duration-200">
          <div className="p-2">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t(title)}
              </div>
              <button
                onClick={handleApply}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {t("Apply")}
              </button>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {options.map((item) => (
                <label
                  key={item}
                  className="flex items-center px-3 py-2.5 cursor-pointer rounded-lg transition-colors duration-150 text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                >
                  <input
                    type="checkbox"
                    checked={pendingSelection.includes(item)}
                    onChange={() => handleToggle(item)}
                    className="w-4 h-4 rounded border-2 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 transition-colors duration-150"
                  />
                  <span className="ml-3 text-sm font-medium">{t(item)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultipleSelector;
